import { useState, useEffect, useRef } from "react";
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { QRCodeSVG } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, RefreshCw, QrCode, Clock, Copy } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { getClientId } from "@/lib/client";

export const PAYPAL_OPTIONS = { clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID, currency: "USD", intent: "capture" };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const idrPrice = (product, credits) => (product === "pro" ? 150000 : (credits || 100) * 750);
const IDR = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

const PayPalSection = ({ product, credits, onSuccess, onClose }) => {
  const [{ isPending, isRejected }, dispatch] = usePayPalScriptReducer();
  if (isPending) {
    return (
      <div className="flex items-center justify-center gap-2 py-3 text-sm text-gray-400" data-testid={`paypal-loading-${product}`}>
        <Loader2 size={15} className="animate-spin" /> Loading PayPal…
      </div>
    );
  }
  if (isRejected) {
    return (
      <button
        data-testid={`paypal-retry-${product}`}
        onClick={() => dispatch({ type: "resetOptions", value: PAYPAL_OPTIONS })}
        className="w-full py-2.5 text-sm font-semibold text-[#C0392B] border border-[#C0392B] rounded-full flex items-center justify-center gap-1.5"
      >
        <RefreshCw size={14} /> PayPal failed — Retry
      </button>
    );
  }
  return (
    <PayPalButtons
      forceReRender={[product, credits]}
      style={{ layout: "vertical", shape: "pill", label: "pay", height: 40 }}
      createOrder={async () => {
        const { data } = await axios.post(
          `${API}/paypal/create-order`,
          { client_id: getClientId(), product, credits: credits || undefined },
          { withCredentials: true }
        );
        return data.order_id;
      }}
      onApprove={async (d) => {
        const { data: res } = await axios.post(`${API}/paypal/capture-order`, { order_id: d.orderID });
        toast.success(product === "pro" ? "PRO unlocked — welcome aboard!" : `${credits || 100} credits added to your balance!`);
        onSuccess(res.client); onClose();
      }}
      onError={() => toast.error("PayPal payment failed. Please try again.")}
    />
  );
};

const Row = ({ label, value, strong }) => (
  <div className="flex items-center justify-between py-1.5 text-sm">
    <span className="text-gray-500">{label}</span>
    <span className={`font-medium text-gray-900 ${strong ? "font-black text-base" : ""}`}>{value}</span>
  </div>
);

const QrisReceipt = ({ qris, onExpired, onPaid }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const remaining = Math.max(0, new Date(qris.expired_at).getTime() - now);
  const expired = remaining <= 0;
  const mm = String(Math.floor(remaining / 60000)).padStart(2, "0");
  const ss = String(Math.floor((remaining % 60000) / 1000)).padStart(2, "0");

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text).then(() => toast.success(`${label} disalin`)).catch(() => void 0);
  };

  if (expired) {
    return (
      <div className="text-center py-3">
        <p className="text-sm text-red-500 mb-2">QR kedaluwarsa.</p>
        <button onClick={onExpired} className="text-sm font-bold text-[#C0392B] underline">Buat QR baru</button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col items-center mb-4">
        <div className="p-3 bg-white rounded-xl border border-gray-200" data-testid="qris-image">
          <QRCodeSVG value={qris.qr_string || ""} size={200} bgColor="#ffffff" fgColor="#15171a" level="M" />
        </div>
        <p className="text-xs text-gray-500 mt-2">Scan dengan e-wallet / m-banking apa pun</p>
      </div>

      <div className="rounded-lg border border-gray-100 px-3 py-1 bg-gray-50/50">
        <Row label="ID Transaksi" value={
          <button onClick={() => copy(qris.invoice, "ID Transaksi")} className="flex items-center gap-1 hover:text-[#C0392B]">
            {qris.invoice} <Copy size={11} className="text-gray-300" />
          </button>
        } />
        <Row label="Order ID" value={
          <button onClick={() => copy(qris.order_id, "Order ID")} className="flex items-center gap-1 hover:text-[#C0392B]" title={qris.order_id}>
            {qris.order_id.slice(0, 8)}… <Copy size={11} className="text-gray-300" />
          </button>
        } />
        <Row label="Nominal" value={IDR(qris.amount)} />
        <Row label="Biaya Admin" value={IDR(qris.fee)} />
        <div className="border-t border-gray-200 my-1" />
        <Row label="Total Bayar" value={IDR(qris.total_payment)} strong />
      </div>

      <div className="flex items-center justify-center gap-1.5 mt-3 text-xs text-gray-500">
        <Clock size={12} /> Bayar dalam <span className="font-mono font-bold text-[#C0392B]">{mm}:{ss}</span>
      </div>
      <div className="flex items-center justify-center gap-2 mt-3 text-xs text-gray-400">
        <Loader2 size={12} className="animate-spin" /> Menunggu pembayaran…
      </div>
    </div>
  );
};

export const PaymentModal = ({ open, onOpenChange, product, credits, onSuccess }) => {
  const [qris, setQris] = useState(null);
  const [loadingQris, setLoadingQris] = useState(false);
  const pollRef = useRef(null);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  useEffect(() => {
    if (!qris) return;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/pakasir/order/${qris.order_id}`);
        if (data.status === "completed") {
          stopPoll();
          toast.success(product === "pro" ? "PRO unlocked via QRIS!" : `${credits || 100} credits added via QRIS!`);
          onSuccess(data.client); onOpenChange(false);
        }
      } catch { void 0; }
    };
    pollRef.current = setInterval(poll, 4000);
    return stopPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qris]);

  useEffect(() => { if (!open) { setQris(null); stopPoll(); } }, [open]);

  const createQris = async () => {
    setLoadingQris(true);
    try {
      const { data } = await axios.post(
        `${API}/pakasir/create-order`,
        { client_id: getClientId(), product, credits: credits || undefined },
        { withCredentials: true }
      );
      setQris(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "QRIS gagal dibuat.");
    } finally {
      setLoadingQris(false);
    }
  };

  const total = idrPrice(product, credits);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="payment-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Pilih Metode Pembayaran
          </DialogTitle>
          <DialogDescription>Bayar via QRIS (Indonesia) atau PayPal.</DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <QrCode size={18} className="text-[#C0392B]" />
            <span className="font-semibold text-gray-900">QRIS</span>
            <span className="text-xs text-gray-400">untuk Indonesia</span>
          </div>
          {!qris ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-500">Total</span>
                <span className="text-lg font-black text-gray-900" data-testid="qris-total">{IDR(total)}</span>
              </div>
              <button
                data-testid="qris-pay-btn"
                onClick={createQris}
                disabled={loadingQris}
                className="w-full py-2.5 text-sm font-bold bg-[#C0392B] text-white rounded-full hover:bg-[#A93226] disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
              >
                {loadingQris ? <><Loader2 size={15} className="animate-spin" /> Membuat…</> : <><QrCode size={15} /> Bayar dengan QRIS</>}
              </button>
            </>
          ) : (
            <QrisReceipt qris={qris} onExpired={() => { setQris(null); stopPoll(); }} />
          )}
        </div>

        <div className="flex items-center gap-3 my-2">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-xs text-gray-400">atau</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        <div className="rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="font-semibold text-gray-900 text-sm">PayPal</span>
            <span className="text-xs text-gray-400">USD</span>
          </div>
          <PayPalSection product={product} credits={credits} onSuccess={onSuccess} onClose={() => onOpenChange(false)} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
